import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const ADDED_STATE_DURATION = 2000;

/**
 * Wraps a plain cart form in a product card and adds the item without a page
 * load. Follows the same contract as `product-form-component`: dispatches a
 * `CartLinesUpdateEvent` whose promise resolves with the refreshed cart and the
 * re-rendered cart sections, so the cart drawer, cart icon and cart items update
 * (and the drawer auto-opens when enabled). Without JavaScript the form posts
 * to /cart/add as usual.
 *
 * The event is dispatched only after Shopify accepted the item: an early event
 * opens the drawer optimistically, and on an error (sold out, stock limit) the
 * modal drawer would then cover the card without any hint. Errors show
 * Shopify's reason in the card's `[data-add-status]` element instead
 * (fallback text: `data-error`) and only sync the cart, without the drawer.
 */
class KunnieAddButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;

  connectedCallback() {
    this.#controller = new AbortController();
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timeout);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    this.#setStatus('');

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      if (response.status) {
        this.#setStatus(response.description || response.message || this.dataset.error || '');
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        // A 422 can still add part of the quantity ("nur 50 Artikel hinzugefügt"): sync cart icon and drawer
        // with action 'update', which does not open the drawer over the message.
        const line = cart.items.find((item) => String(item.variant_id) === String(formData.get('id')));
        if (line) {
          const deferred = CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'update',
              context: 'product',
              lines: [{ id: line.key, quantity: line.quantity }],
              promise: deferred.promise,
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'kunnie-add-button', productId: this.dataset.productId, didError: true },
          });
        }
        return;
      }

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'kunnie-add-button',
          sourceId: this.dataset.productId,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      this.#showAdded();
    } catch (error) {
      this.#setStatus(this.dataset.error || '');
      this.dispatchEvent(
        new CartErrorEvent({ error: error instanceof Error ? error.message : 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  /** @param {string} text */
  #setStatus(text) {
    const status = this.closest('.kunnie-card, .kunnie-mini')?.querySelector('[data-add-status]');
    if (status) status.textContent = text;
  }

  #showAdded() {
    clearTimeout(this.#timeout);
    this.dataset.state = 'added';
    this.#timeout = setTimeout(() => delete this.dataset.state, ADDED_STATE_DURATION);
  }
}

if (!customElements.get('kunnie-add-button')) {
  customElements.define('kunnie-add-button', KunnieAddButton);
}
